import BaseModal from 'components/BaseModal'
import WizardFormV2 from 'components/WizardFormV2'
import { MARGIN_LG, MODAL_SIZE_MEDIUM } from 'constants/Layout';
import { Tooltip, Alert, Form, Typography } from 'antd';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import ProductAPI from 'restAPI/ProductAPI';
import UserAPI from 'restAPI/UserAPI';
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';
import _ from 'lodash';
import { editorRules, FieldTypes } from 'utils/formUtils';
import { CLASS_TYPE_DEFAULT_OPTIONS, CLASS_TYPE_INFRA_OPTIONS, artifactsValidator, getDefaultTierValues, getOwnerFields, getSecurityAndComplianceFields,appendVersionToName, syncTagsFromTier, syncTierFromTags } from '../common';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import { useGlobalSettingsContext } from 'contexts/GlobalSettingsContext';
import { getGlobalFilter_UNSAFE } from 'contexts/GlobalFilterContext';
import message from 'components/CustomMessage';
import { getKeyValuePair } from 'views/GlobalSettings/TierPresetConfiguration';
import { InfoIcon } from 'components/Icons';
import TeamsAPI from 'restAPI/TeamsAPI';
import { useFetchProductStatus } from './ProductStatus';
import MessageEditor from 'components/MessageEditor';
import SingleCollapsePanel from 'components/SingleCollapsePanel';
import ArtifactsTable from '../ArtifactsTable';
import { PermissionSetMap } from 'constants/RBACConstants';
import SLAPreview from '../SLAPreview';
import { productNameFieldRules, updateDataAfterGet, updateDataBeforePush } from './common';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { CUSTOM_TIER, TIER_OPTIONS, TIER_OPTIONS_WITH_CUSTOM } from 'utils/tierUtil';
import TierBasedTagField from '../TierBasedTagField';
import useTierBasedTags from 'hooks/useTierBasedTags';
import TagTable from 'components/TagTable';
import useAPIResponse from 'hooks/useAPIResponse';
import { useGlobalUserListContext } from 'contexts/GlobalUserListContext';
import { CellRenderers } from 'utils/tableUtils';
import useTierOptions from 'hooks/useTierOptions';
import { ARMORCODE_GROUP_KEY } from 'utils/armorcodeGroupUtil';

export const CATEGORIZATION_DEFAULT_ACTIVE_KEY = ['confidentialityRequirement','confidentiality_custom', '1','availabilityRequirement','availability','attackingVector'];

export default forwardRef(function ProductFormPopup({editId, activeUser, ...props}, ref) {
    const globalSettings = useGlobalSettingsContext();
    const [globalFilters] = useState(getGlobalFilter_UNSAFE);
    const thisRef = useRef();
    const [form, setForm] = useState();
    const [users, setUsers] = useState();
    const [productTypes] = useAPIResponse(() => ProductAPI.getTypes(), []);
    const [existingProduct, setExistingProduct] = useState();
    const [createdProduct, setCreatedProduct] = useState();
    const [confidentialityData, setConfidentialityData] = useState({});
    const [categorization, setCategorization] = useState();
    const [teamsOptions, setTeamOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [slaConfigs, setSlaConfigs] = useState([])
    const [slaID, setSlaID] = useState();
    const defaultActiveKey = (editId)  ? ['confidentialityRequirement'] : CATEGORIZATION_DEFAULT_ACTIVE_KEY;
    const [classTypeOptions, setClassTypeOptions] = useState(CLASS_TYPE_DEFAULT_OPTIONS);
    const [teamId, setTeamId] = useState();
    const { loading: productStatusLoading, statusList } =  useFetchProductStatus();
    const productName = getVocabulary("product");
    const validateAPIRef = useRef();
    const featureFlags = useFeatureFlag();
    const {tierBasedTags, allTierBasedTags} = useTierBasedTags();
    const { refreshUsers } = useGlobalUserListContext();
    const tierOptionsWithCustom = useTierOptions();

    const fetchTeams = useCallback(() => {
        const {promise, cancel} = TeamsAPI.getAllTeams({ teamUser: true });
        const promise2 = promise.then(data => {
            const teams = data.map(d => ({value: d.id, label: d.name}));
            setTeamOptions(teams);
            return teams;
        }, _.noop)
        return { promise: promise2, cancel};
      }, [])

      useEffect(() => {
            const { cancel } = fetchTeams();
            return cancel;
    }, [fetchTeams]);
    
    useEffect(() => {
        if (form && !existingProduct) {
        form.setFieldsValue({status: statusList[0]})
        }
    }, [statusList, form, existingProduct])

    const fetchUsers = useCallback(() => {
        setLoading(true);
        const { promise, cancel } = UserAPI.getAll(editId);
        const promise2 = promise.then(data => {
            const userMap = data.map(d => ({ value: d.id, label: d.displayName }));
            setUsers(userMap);
            setLoading(false);
            return userMap;
        }, _.noop).catch(() => {
            setLoading(false);
        });

        return { promise: promise2, cancel };
    }, [editId]);

    useEffect(() => {
        const { cancel } = fetchUsers();
        return cancel;
    }, [fetchUsers]);

    useEffect(() => {
        const { promise, cancel } = GlobalSettingAPI.getSLAList();
        promise.then(setSlaConfigs);
        return cancel;
    }, []);

    useEffect(() => {
        if (form && !_.isEmpty(productTypes) && _.isUndefined(editId)) {
            form.setFields([{
                name: "type",
                value: _.find(productTypes, { name: CellRenderers.VALUE_OR_NA() })?.id
            }])
        }
    }, [form, productTypes, editId]);
    useEffect(() => {
        if(editId){
            const { cancel, promise } = ProductAPI.get(editId)
            promise.then(updateDataAfterGet).then((data) => {
                setExistingProduct(data);
                setSlaID(data.slaTemplateId);
                setClassTypeOptions(CLASS_TYPE_DEFAULT_OPTIONS.includes(data.classType) ? CLASS_TYPE_DEFAULT_OPTIONS : CLASS_TYPE_INFRA_OPTIONS);
                setTeamId(data.teamDto);
                if(_.isString(data.confidentialityOptions) && data.confidentialityOptions.length > 0){
                    const _confidentialityData = {};
                    data.confidentialityOptions.split(",").forEach((key) => {
                        _confidentialityData[key.trim()] = true;
                    });
                    setConfidentialityData(_confidentialityData);
                }
            }, _.noop);
            return cancel;
        } else {
            setSlaID(_.find(slaConfigs, { name: 'Default SLA' })?.id);
        }
    }, [editId, slaConfigs]);

    const onNext = useCallback(() => {
        if(!form || editId){
            return;
        }
        const currentCategorization = form.getFieldValue('tier');
        if(!currentCategorization || currentCategorization === categorization){
            return;
        }
        const { selectedTierGlobalFields,  categorizationConfidentiality} = getDefaultTierValues(currentCategorization, globalSettings.tierCategory);
        const defaultResponses = _.cloneDeep(getKeyValuePair(selectedTierGlobalFields))
        form.setFields(defaultResponses);
        setConfidentialityData(categorizationConfidentiality);
        setCategorization(currentCategorization);
    },[editId,form, categorization, globalSettings.tierCategory]);

    useImperativeHandle(ref, () => ({ ...thisRef.current }));
    
    const onSuccess = useCallback((data) => {
        setCreatedProduct(data);
        message.success(
            <>
                {!editId ? "Created" : "Edited"} {productName.toLowerCase()} - {data.name} 
                {(!editId && (globalFilters.globalFilterSubProducts.length > 0 || globalFilters.globalFilterGroups.length > 0) ) &&
                <Typography style={{ color: 'white' }}>Please update the Global Filters in order to view recently created {productName.toLowerCase()}</Typography>}
            </>
        );
        thisRef.current.close();
    }, [editId, productName, globalFilters.globalFilterSubProducts,  globalFilters.globalFilterGroups]);
    
    const onConfidentialityChange = useCallback((e, id) => {
        const _confidentialityData = {...confidentialityData};
        id = "C"+id;
        if(e.target.checked){
            _confidentialityData[id] = true;
        } else {
            delete _confidentialityData[id];
        }
        setConfidentialityData(_confidentialityData);
    }, [confidentialityData]);

    const modifyData = useCallback((data) => {
        data = updateDataBeforePush(data, confidentialityData, globalSettings.tierCategory);
        return data;
    }, [confidentialityData, globalSettings.tierCategory]);

    const securityAndComplianceData = getSecurityAndComplianceFields({
        onConfidentialityChange,
        confidentialityData,
        defaultActiveKey
    });

    const onChange = (values) => {
        if(values.hasOwnProperty('slaTemplateId')){
            setSlaID(values.slaTemplateId);
        }
        
        if (values.hasOwnProperty('teamDto')) {
            setTeamId(values.teamDto);
        }
    };

    const getSLAFields = useMemo(() => (
        [
            {
                name: "slaTemplateId",
                label: <>SLA Name <Tooltip title="If SLA is not selected, then default (global) SLA will be applied."><InfoIcon className="m-l-xs"/></Tooltip></>,
                placeholder: "SLA Name",
                type: FieldTypes.SELECT,
                props: {
                    allowClear: true,
                    defaultValue: slaID,
                },
                options: slaConfigs

            }, {
                name: "slaPreview",
                render: () => <SLAPreview slaID={slaID} viewOnly={!slaID} />
            }
        ]
    ), [slaConfigs, slaID])

    const getBasicInfoFields = useMemo(() => {
        return [...((!_.isEmpty(globalFilters.globalFilterProducts) || !_.isEmpty(globalFilters.globalFilterGroups)) && !editId  ? [{
            name: "warning",
            type: FieldTypes.LABEL,
            render: () => (
                <Alert className='m-b-sm' message = {<>In order to view newly created <Vocabulary name="product" isPlural />, make sure to include them as part of the Global Filters.</>} type="warning" />
            )
        }] : []),{
            name: "name",
            label: "Name",
            colSpan: 12,
            NoDefaultLengthRule: true,
            props: {
                autoFocus: true
            },
            rules: productNameFieldRules(appendVersionToName(existingProduct), validateAPIRef, form, featureFlags.VERSIONS),
        }, {
            name: "type",
            label: "Type",
            type: FieldTypes.SELECT,
            colSpan: 12,
            options: productTypes,
            loading: _.isUndefined(productTypes),
            props: {
                onChange: value => {
                    const fieldObj = {...form.getFieldValue()};
                    fieldObj.type = value;
                    let opts = classTypeOptions;
                    const obj = _.find(productTypes, {id: value});
                    if (obj.name.toLowerCase() === "infrastructure") {
                        fieldObj.classType = CLASS_TYPE_INFRA_OPTIONS[0];
                        opts = CLASS_TYPE_INFRA_OPTIONS;
                    } else {
                        if (!CLASS_TYPE_DEFAULT_OPTIONS.includes(fieldObj.classType)) {
                            fieldObj.classType = CLASS_TYPE_DEFAULT_OPTIONS[0];
                            opts = CLASS_TYPE_DEFAULT_OPTIONS;
                        }
                    }
                    setClassTypeOptions(opts);
                    form.setFieldsValue(fieldObj);
                }
            }
        }, {
            name: "description",
            label: "Description",
            type: FieldTypes.TEXT_AREA,
            NoDefaultLengthRule: true,
            render: () => {
                return (
                    <MessageEditor
                        placeholder={'Description'}
                        toolBarIdentifier={'message-editor-product-description'}
                        key={'product-description'}
                        hideEmoji
                        hideOldUpload
                        alwaysOpen
                        showFooterButtons={false}
                    />
                );
            },
            rules: [editorRules({required: false, min: 0})]
        }, {
            name: "status",
            label: "Status",
            type: FieldTypes.SELECT,
            colSpan: 12,
            loading: productStatusLoading,
            options: statusList,
        }, {
            name: "versionNumber",
            label: "Version Number",
            NoDefaultLengthRule: true,
            help: <>Please enter <Vocabulary name="product" isLowerCase={true} /> version e.g. 1.0 or 0.1.23</>,
            colSpan: 12,
            rules: [{
                max: 128,
                message: "Maximum length is 128 characters"
            }]
        },
        {
            name: 'tier',
            label: 'Tier',
            type: FieldTypes.SELECT,
            colSpan: 12,
            options:  featureFlags.RISK_TIERS ? tierOptionsWithCustom : featureFlags.RISK_SCORE_3_0 ? TIER_OPTIONS_WITH_CUSTOM : TIER_OPTIONS,
            props: {
                onChange: (tier) => {
                    if(featureFlags.RISK_SCORE_3_0){
                        const tierTags = featureFlags.RISK_TIERS ? _.find(tierBasedTags, { id: tier })?.tags : tierBasedTags[tier];
                        syncTagsFromTier({
                            oldTags: form.getFieldValue("tierTags"),
                            newTier: tier,
                            tierBasedTags,
                            tags: form.getFieldValue("tags"),
                            form,
                            otherValues: { tierTags },
                            isNewCustomTiers: featureFlags.RISK_TIERS,
                        });
                    }
                }
            }
        }, featureFlags.RISK_SCORE_3_0 ? {
            name: "tierTags",
            render: () => (
                <TierBasedTagField
                    allTags={allTierBasedTags}
                    tier={form?.getFieldValue("tier") || CUSTOM_TIER}
                    tierBasedTags={tierBasedTags}
                    onChange={(tags, { deleted, added }) => {
                        syncTagsFromTier({
                            oldTags: deleted,
                            newTags: added,
                            tierBasedTags,
                            tags: form.getFieldValue("tags"),
                            form,
                            otherValues: { tier: CUSTOM_TIER },
                            isNewCustomTiers: featureFlags.RISK_TIERS,
                        });
                    }}
                />
            ),
            style: { display: "none" },
        } : undefined,
        {
            name: "classType",
            label: "Class",
            type: FieldTypes.SELECT,
            colSpan: 12,
            options: classTypeOptions
        },
        {
            name: "tags",
            render: () => {
                return <TagsField onChange={(tags, { deleted }) => {
                    if(featureFlags.RISK_SCORE_3_0){
                        syncTierFromTags({ deletedTag: deleted, form });
                    }
                }} />;
            }
        },
        {
            name: "artifacts",
            render: () => {
                return <ArtifactsField form={form} />
            }
        }
     ].filter(f => !_.isEmpty(f))
    }, [globalFilters.globalFilterProducts, globalFilters.globalFilterGroups, editId, existingProduct, form, productTypes, productStatusLoading, statusList, featureFlags.RISK_TIERS, featureFlags.RISK_SCORE_3_0, tierOptionsWithCustom, classTypeOptions, tierBasedTags, allTierBasedTags])

    const pages = [{
        name: "Basic Information",
        fields: getBasicInfoFields
    }];

    pages.push({
        name: "SLA",
        fields: getSLAFields
    },{
        name: "Risk Assessment",
        fields: featureFlags.RISK_SCORE_3_0
            ? 
            [
                _.find(getBasicInfoFields, { name: "tier" }),
                {
                  ..._.find(getBasicInfoFields, { name: "tierTags" }),
                  style: { marginBottom: MARGIN_LG },
                },
                {
                  ..._.find(getBasicInfoFields, { name: "tags" }),
                  style: { display: "none" },
                },
            ]
            : securityAndComplianceData.fields,
    }, {
        name: "Owners",
        fields: getOwnerFields({ users, activeUser, form, fetchUsers, type: "product", loading, teamsOptions, fetchTeams, teamId, setTeamId, refreshUsers })
    });

    const initialValues = {
        classType: "Software only",
    }
    if(productTypes && !editId){
        initialValues.type = _.find(productTypes, { name: "Web Application" })?.id;
    }
    return (
        <BaseModal
            modalProps={{
                title: <>{!editId ? "Add" : "Edit"} <Vocabulary name="product" /></>,
                footer: null,
                width: MODAL_SIZE_MEDIUM,
                className: "modal-full-height no-footer"
                // style: { top: 20 },
            }}
            ref={thisRef}
            showExitWarning
            afterClose={() => props.afterClose(createdProduct)}
        >
            <WizardFormV2 
                isEdit={!_.isUndefined(editId)}
                onCancel={thisRef.current?.close}
                onChange={onChange}
                pages={pages}
                initialValues={editId ?
                    {
                        ...existingProduct,
                    } :
                    {
                        ...initialValues,
                        ...securityAndComplianceData.initialValues
                    }}
                formRef={setForm}
                api={ProductAPI[!editId ? "save" : "update"]}
                beforeAPI={modifyData}
                onSuccess={onSuccess}
                validator={parseErrorMessage}
                loading={editId && _.isEmpty(existingProduct)}
                onNext = {onNext}
                showSkipSaveBtnFromStep={0}
            />
        </BaseModal>
    )
});

function parseErrorMessage(e){
    const message = _.lowerCase(e.response?.data?.message ?? "");
    if(message.startsWith("product with name")){
        return [{
            name: "name",
            errors: [message]
        }]
    }
}

export function validateTagsAreSaved(tags, message = 'Please save the tags.') {
    const isEdit = !_.isEmpty(tags) && _.some(tags, (tag) => tag.isEdit);
    if (isEdit) {
        return Promise.reject(message);
    }
    return Promise.resolve();
}

export function validateTagLength(tags, message = 'key+value length should be less than 996 characters.') {
    const length = 995;
    const lengthExceeded = !_.isEmpty(tags) && _.some(tags, (tag) => (tag.key + (tag.value || "")).length > length);
    if (lengthExceeded) {
        return Promise.reject(message);
    }
    return Promise.resolve();
}

export function TagsField({ staticTags, onChange, value }) {
    const source = useMemo(() => _.uniq([...(staticTags || []), ...(value || [])]), [value, staticTags]);
    const tableRef = useRef();
    const onTagsChange = useCallback((tags, {deleted}) => {
        const toString = (tag) => `${tag.key}${tag.value ? `:${tag.value}` : ''}`;
        const extraData = {};
        if(deleted){
            extraData.deleted = toString(deleted);
        }
        onChange(_.map(tags, toString), extraData);
    }, [onChange]);

    return (
        <>
            <SingleCollapsePanel header="Tags" className='m-b-xs'>
                <Alert
                    description={<TagTable className="p-sm" source={source} editable onChange={onTagsChange} tagsRef={tableRef} staticTags={staticTags} accountAdminOnlyKeys={[ARMORCODE_GROUP_KEY]} />}
                    type="info"
                    style={{ margin: -16 }}
                />
            </SingleCollapsePanel>
            <Form.Item className='dummy-form-item m-b-m' name={"tags_validator"} rules={[{
                validator: () => validateTagsAreSaved(tableRef.current?.getTags())
            },
            {
                validator: () => {
                    return validateTagLength(tableRef.current?.getTags());
                },
            }]} />
        </>
    )
}

export function ArtifactsField({form, value }) {
    const [source] = useState(value);
    const tableRef = useRef();

    return (
        <>
            <SingleCollapsePanel header="Artifacts" className='m-b-xs'>
                <Alert
                    description={(
                        <ArtifactsTable 
                            parentDataSourceRef={tableRef}
                            form={form}
                            source={source}
                            editable permission={PermissionSetMap.UPDATE_PRODUCT}
                        />
                    )}
                    type="info"
                    style={{ margin: -16 }}
                />
            </SingleCollapsePanel>
            <Form.Item className='dummy-form-item m-b-m' name={"artifacts_validator"} rules={[{
                validator: () => artifactsValidator(tableRef.current?.getData())
            }]} />
        </>
    )
}
